import { IntegrationGroup, IntegrationResponse, IntegrationType } from '@business/entities/integration';
import { CommonService } from '@business/services';
import { FormCheckbox, FormInput, FormSelect } from '@core/components/form';
import { ModalAction, ModalContent, ModalDrawer, ModalFooter, ModalHeader } from '@core/components/Modal';
import { useModalInstance } from '@core/components/ModalStack';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormDataType, formGroup, useFormConfig, useFormState } from '@core/utils/form';
import { useObservable } from '@core/utils/hooks/rxjs';
import { css } from '@emotion/react';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { tap } from 'rxjs/operators';
import { t } from 'ttag';
import { TaxjarOverlayBloc } from './TaxjarOverlayBloc';

export enum TaxJarTaxType {
  location = 'location',
  order = 'order',
}

export function defineTaxjarForm() {
  return formGroup({
    apiKey: formControl<string>({ required: true, label: 'API Token' }),
    salesTax: formControl<boolean>({ label: 'Sales tax calculation', initialValue: true }),
    salesTaxType: formControl<string>({ label: 'API Endpoint', initialValue: 'location' }),
    syncOrders: formControl<boolean>({ label: 'Sync orders', initialValue: false, disabled: true }),
    syncCustomers: formControl<boolean>({ label: 'Sync customers', initialValue: false, disabled: true }),
  });
}

export type TaxjarFormData = FormDataType<typeof defineTaxjarForm>;

const styles = {
  content: css`
    padding: 25px !important;
  `,
};

export interface TaxjarOverlayProps {
  integration?: IntegrationResponse;
}

export const TaxjarOverlay = memo((props: TaxjarOverlayProps) => {
  const { integration } = props;
  const modal = useModalInstance();
  const bloc = useBloc(TaxjarOverlayBloc);
  const form = useMemo(() => defineTaxjarForm(), []);
  const formValue = useObservable(form.value$);
  const isEdit = useMemo(() => !!integration, [integration]);

  const typeItems = useMemo(
    () =>
      CommonService.selectItems(
        [
          { name: 'location', label: 'Sales Tax by Location' },
          { name: 'order', label: 'Sales Tax by Order Information' },
        ],
        'label',
        'name',
      ),
    [],
  );

  const onSubmit = useCallback(
    (data: TaxjarFormData) => {
      if (isEdit) {
        return bloc.createIntegration({ group: IntegrationGroup.tax, type: IntegrationType.taxjar, settings: data }).pipe(tap(() => modal.close()));
      }
      return bloc.createIntegration({ group: IntegrationGroup.tax, type: IntegrationType.taxjar, settings: data }).pipe(tap(() => modal.close()));
    },
    [bloc, isEdit, modal],
  );
  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  useEffect(() => {
    if (integration) {
      form.patchValue(integration.settings as TaxjarFormData);
    }
  }, [form, integration]);

  return (
    <ModalDrawer maxWidth="md">
      <ModalHeader title="Taxjar" />
      <ModalContent formContainer={{ onSubmit: form.submit }} customCss={styles.content}>
        <FormInput control={form.controls.apiKey} />
        <FormCheckbox control={form.controls.salesTax} />
        {formValue?.salesTax && (
          <>
            <FormSelect items={typeItems} control={form.controls.salesTaxType} />
          </>
        )}
        <FormCheckbox control={form.controls.syncOrders} disabled />
        <FormCheckbox control={form.controls.syncCustomers} disabled />
      </ModalContent>
      <ModalFooter>
        <ModalAction intent="primary" text={t`Save`} loading={submitting} autoClose={false} onClick={form.submit} />
      </ModalFooter>
    </ModalDrawer>
  );
});
